import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const BulkActionModal = ({ isOpen, onClose, selectedProducts, action, onConfirm }) => {
  const [formData, setFormData] = useState({
    priceAdjustment: '',
    adjustmentType: 'percentage',
    inventoryAdjustment: '',
    newStatus: 'active'
  });
  const [loading, setLoading] = useState(false);

  const adjustmentTypeOptions = [
    { value: 'percentage', label: 'Percentage (%)' },
    { value: 'fixed', label: 'Fixed Amount ($)' }
  ];

  const statusOptions = [
    { value: 'active', label: 'Active' },
    { value: 'inactive', label: 'Inactive' },
    { value: 'draft', label: 'Draft' }
  ];

  const getActionConfig = () => {
    switch (action) {
      case 'activate':
        return {
          title: 'Activate Products',
          description: 'Set selected products to active status',
          icon: 'CheckCircle',
          color: 'text-success'
        };
      case 'deactivate':
        return {
          title: 'Deactivate Products',
          description: 'Set selected products to inactive status',
          icon: 'XCircle',
          color: 'text-warning'
        };
      case 'update_price':
        return {
          title: 'Update Prices',
          description: 'Adjust prices for selected products',
          icon: 'DollarSign',
          color: 'text-primary'
        };
      case 'update_inventory':
        return {
          title: 'Update Inventory',
          description: 'Adjust inventory levels for selected products',
          icon: 'Package',
          color: 'text-primary'
        };
      case 'archive':
        return {
          title: 'Archive Products',
          description: 'Move selected products to archive',
          icon: 'Archive',
          color: 'text-error'
        };
      case 'export':
        return {
          title: 'Export Products',
          description: 'Download selected products data',
          icon: 'Download',
          color: 'text-primary'
        };
      default:
        return {
          title: 'Bulk Action',
          description: 'Perform action on selected products',
          icon: 'Settings',
          color: 'text-primary'
        };
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await onConfirm(action, formData);
      onClose();
    } catch (error) {
      console.error('Bulk action error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  if (!isOpen) return null;

  const config = getActionConfig();

  return (
    <div className="fixed inset-0 z-1200 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen p-4">
        <div className="fixed inset-0 bg-black/50" onClick={onClose} />
        
        <div className="relative bg-card border border-border rounded-lg w-full max-w-md">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-border">
            <div className="flex items-center space-x-3">
              <Icon name={config.icon} size={24} className={config.color} />
              <h2 className="text-xl font-semibold text-foreground">
                {config.title}
              </h2>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={onClose}
              className="micro-animation"
            >
              <Icon name="X" size={20} />
            </Button>
          </div>

          {/* Content */}
          <form onSubmit={handleSubmit}>
            <div className="p-6 space-y-6">
              {/* Description */}
              <div className="bg-muted/50 rounded-lg p-4">
                <p className="text-sm text-muted-foreground mb-2">
                  {config.description}
                </p>
                <div className="flex items-center space-x-2">
                  <Icon name="Package" size={16} className="text-muted-foreground" />
                  <span className="text-sm font-medium text-foreground">
                    {selectedProducts.length} product{selectedProducts.length !== 1 ? 's' : ''} selected
                  </span>
                </div>
              </div>

              {/* Action-specific forms */}
              {action === 'update_price' && (
                <div className="space-y-4">
                  <Select
                    label="Adjustment Type"
                    options={adjustmentTypeOptions}
                    value={formData.adjustmentType}
                    onChange={(value) => handleInputChange('adjustmentType', value)}
                    required
                  />
                  
                  <Input
                    label={`Price ${formData.adjustmentType === 'percentage' ? 'Percentage' : 'Amount'}`}
                    type="number"
                    placeholder={formData.adjustmentType === 'percentage' ? '10' : '5.00'}
                    value={formData.priceAdjustment}
                    onChange={(e) => handleInputChange('priceAdjustment', e.target.value)}
                    required
                    min="0"
                    step={formData.adjustmentType === 'percentage' ? '1' : '0.01'}
                    description={
                      formData.adjustmentType === 'percentage' ?'Enter percentage to increase/decrease (use negative for decrease)' :'Enter fixed amount to add/subtract (use negative to subtract)'
                    }
                  />
                </div>
              )}

              {action === 'update_inventory' && (
                <div>
                  <Input
                    label="Inventory Adjustment"
                    type="number"
                    placeholder="10"
                    value={formData.inventoryAdjustment}
                    onChange={(e) => handleInputChange('inventoryAdjustment', e.target.value)}
                    required
                    description="Enter amount to add/subtract (use negative to subtract)"
                  />
                </div>
              )}

              {(action === 'activate' || action === 'deactivate') && (
                <div className="bg-warning/10 border border-warning/20 rounded-lg p-4">
                  <div className="flex items-start space-x-3">
                    <Icon name="AlertTriangle" size={20} className="text-warning mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-foreground">
                        Confirm Status Change
                      </p>
                      <p className="text-sm text-muted-foreground mt-1">
                        This will {action === 'activate' ? 'activate' : 'deactivate'} all selected products.
                        {action === 'activate' && ' Active products will be visible to customers.'}
                        {action === 'deactivate' && ' Inactive products will be hidden from customers.'}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {action === 'archive' && (
                <div className="bg-error/10 border border-error/20 rounded-lg p-4">
                  <div className="flex items-start space-x-3">
                    <Icon name="AlertTriangle" size={20} className="text-error mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-foreground">
                        Archive Products
                      </p>
                      <p className="text-sm text-muted-foreground mt-1">
                        Archived products will be moved to the archive section and hidden from the main catalog. 
                        This action can be reversed later.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {action === 'export' && (
                <div className="bg-primary/10 border border-primary/20 rounded-lg p-4">
                  <div className="flex items-start space-x-3">
                    <Icon name="Download" size={20} className="text-primary mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-foreground">
                        Export Products
                      </p>
                      <p className="text-sm text-muted-foreground mt-1">
                        Selected products will be exported as a CSV file containing all product details 
                        including name, price, inventory, and other attributes.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Product List Preview */}
              <div className="max-h-32 overflow-y-auto border border-border rounded-lg">
                <div className="p-3">
                  <p className="text-sm font-medium text-foreground mb-2">
                    Selected Products:
                  </p>
                  <div className="space-y-1">
                    {selectedProducts.slice(0, 5).map((product) => (
                      <div key={product.id} className="text-sm text-muted-foreground">
                        • {product.name}
                      </div>
                    ))}
                    {selectedProducts.length > 5 && (
                      <div className="text-sm text-muted-foreground">
                        ... and {selectedProducts.length - 5} more
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end space-x-3 p-6 border-t border-border bg-muted/30">
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant={action === 'archive' ? 'destructive' : 'default'}
                loading={loading}
                iconName={config.icon}
                iconPosition="left"
                iconSize={16}
              >
                {action === 'export' ? 'Export' : 'Apply Changes'}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default BulkActionModal;